<template>
  <div class="grid h-screen place-items-center px-4">
    <div class="w-full max-w-md text-center">
      <img
        class="mx-auto mb-9 h-16 text-center"
        :src="`${assetsUrl}/app-configs/ryder/logo.svg`"
        alt="Ryder Logo"
      />

      <div class="mt-6 flex flex-col">
        <div v-if="appId">
          <transition name="fade">
            <div
              v-if="!done"
              class="flex flex-col gap-y-6"
            >
              <p
                v-if="stage !== 'error' && errorMessage"
                class="text-sm text-red-600 dark:text-red-400"
              >
                {{ errorMessage }}
              </p>

              <p
                v-if="stage === 'loading'"
                class="text-sm text-gray-600 dark:text-gray-300"
              >
                Loading Ryder setup...
              </p>

              <p
                v-else-if="stage === 'error'"
                class="text-sm text-red-600 dark:text-red-400"
              >
                {{ errorMessage }}
              </p>

              <div
                v-else-if="!isAuthenticated"
                class="flex flex-col gap-y-6"
              >
                <div class="flex flex-row justify-center">
                  <UButton
                    color="neutral"
                    :loading="stage === 'connecting'"
                    @click="startOAuth()"
                  >
                    Connect Ryder
                  </UButton>
                </div>
              </div>

              <div
                v-else
                class="flex flex-col gap-y-6"
              >
                <div class="flex flex-col gap-y-3 text-left">
                  <USelectMenu
                    v-model="selectedCustomer"
                    :items="customerOptions"
                    :loading="customersLoading"
                    :disabled="loading || customersLoading"
                    placeholder="Select Ryder customer"
                  />

                  <p
                    v-if="customersError"
                    class="text-sm text-red-600 dark:text-red-400"
                  >
                    {{ customersError }}
                  </p>
                </div>

                <div class="flex flex-row justify-center">
                  <UButton
                    color="neutral"
                    :loading="stage === 'saving'"
                    :disabled="!canSubmit"
                    @click="submit()"
                  >
                    Connect to Yuzu
                  </UButton>
                </div>
              </div>
            </div>

            <div v-else>App installed. Please head back to Yuzu.</div>
          </transition>
        </div>

        <div
          v-else
          class="text-bold text-center text-red-600"
        >
          Invalid app
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const route = useRoute()
const { assetsUrl, baseUrlCore } = useRuntimeConfig().public

const appId = computed(() => typeof route.query.app === 'string' ? route.query.app : undefined)

const done = ref(false)
const stage = ref<'loading' | 'idle' | 'connecting' | 'saving' | 'error'>('loading')
const isAuthenticated = ref(false)
const selectedCustomer = ref<{ label: string, value: number }>()
const customerOptions = ref<{ label: string, value: number }[]>([])
const customersLoading = ref(false)
const customersError = ref('')
const errorMessage = ref('')

const loading = computed(() => ['loading', 'connecting', 'saving'].includes(stage.value))
const canSubmit = computed(() => Boolean(selectedCustomer.value) && !loading.value && !customersLoading.value && !customersError.value)

const getErrorMessage = (error: unknown, fallback = 'Something went wrong. Please try again.') => {
  if (error && typeof error === 'object' && 'data' in error) {
    const data = error.data

    if (
      data
      && typeof data === 'object'
      && 'statusMessage' in data
      && typeof data.statusMessage === 'string'
    ) {
      return data.statusMessage
    }
  }

  return error instanceof Error ? error.message : fallback
}

const loadCustomers = async () => {
  if (!appId.value || !isAuthenticated.value) return

  customersLoading.value = true
  customersError.value = ''

  try {
    const response = await $fetch<{ customers: Array<{ id: number, name: string }> }>('/api/customers', {
      query: { appId: appId.value },
    })

    customerOptions.value = response.customers.map((customer) => ({
      label: customer.name,
      value: customer.id,
    }))

    if (selectedCustomer.value) {
      selectedCustomer.value = customerOptions.value.find((customer) => customer.value === selectedCustomer.value?.value) ?? selectedCustomer.value
    } else if (customerOptions.value.length === 1) {
      selectedCustomer.value = customerOptions.value[0]
    }
  } catch (error) {
    customersError.value = getErrorMessage(error, 'Ryder customers could not be loaded.')
  } finally {
    customersLoading.value = false
  }
}

const loadSetupStatus = async () => {
  if (!appId.value) return

  stage.value = 'loading'
  errorMessage.value = ''

  try {
    const status = await $fetch<{
      auth: {
        connected: boolean
        environment: 'sandbox' | 'production' | null
      }
      account: {
        customerId: number
        customerName: string
      } | null
    }>('/api/setup-status', {
      query: { appId: appId.value },
    })

    isAuthenticated.value = status.auth.connected

    selectedCustomer.value = status.account
      ? {
          label: status.account.customerName,
          value: status.account.customerId,
        }
      : undefined

    stage.value = 'idle'

    await loadCustomers()
  } catch (error) {
    errorMessage.value = getErrorMessage(error)
    stage.value = 'error'
  }
}

const startOAuth = async () => {
  if (!appId.value) return

  stage.value = 'connecting'
  errorMessage.value = ''

  try {
    const response = await $fetch<{ authorizationUrl: string }>('/api/auth/start', {
      method: 'POST',
      body: {
        appId: appId.value,
      },
    })

    await navigateTo(response.authorizationUrl, { external: true })
  } catch (error) {
    errorMessage.value = getErrorMessage(error, 'Ryder authorization could not be started.')
    stage.value = 'error'
  }
}

const submit = async () => {
  if (!appId.value || !selectedCustomer.value) return

  stage.value = 'saving'

  try {
    await $fetch('/api/setup', {
      method: 'POST',
      body: {
        appId: appId.value,
        customerId: selectedCustomer.value.value,
      },
    })
    await $fetch('/api/connect', {
      method: 'POST',
      body: { appId: appId.value },
    })

    await navigateTo(`${baseUrlCore}/apps/${appId.value}`, {
      open: { target: '_blank' },
    })

    done.value = true
    window.close()
  } catch (error) {
    errorMessage.value = getErrorMessage(error)
    stage.value = 'error'
  }
}

onMounted(async () => {
  if (!appId.value) return

  await loadSetupStatus()

  if (route.query.oauth_error && stage.value !== 'error') {
    errorMessage.value = 'Ryder authorization failed. Try connecting again.'
  }
})
</script>
